import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Cake as BirthdayIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Category as IndustryIcon,
} from '@mui/icons-material';

/**
 * ContactCard Component
 * Displays individual family member contact information
 * Click card to view full details in a dialog
 */
const ContactCard = ({ contact }) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const {
    displayName,
    email,
    phoneNumber,
    address,
    photoURL,
    groups,
    isCurrentUser,
    relationship,
    birthday,
    occupation,
    professionalProfile,
    businessInfo,
  } = contact;

  // Format address for display
  const formatAddress = (addr) => {
    if (!addr) return null;
    const parts = [
      addr.street,
      addr.city,
      addr.state,
      addr.zip
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formattedAddress = formatAddress(address);

  // Validate email format to prevent XSS
  const isValidEmail = (emailStr) => {
    if (!emailStr || typeof emailStr !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailStr) && !emailStr.includes(':');
  };

  // Validate phone format to prevent XSS
  const isValidPhone = (phoneStr) => {
    if (!phoneStr || typeof phoneStr !== 'string') return false;
    // Allow digits, spaces, dashes, parentheses, and plus sign only
    const phoneRegex = /^[\d\s\-()+ ]+$/;
    return phoneRegex.test(phoneStr);
  };

  // Handle quick actions
  const handleEmail = (e) => {
    e.stopPropagation();
    if (email && isValidEmail(email)) {
      window.location.href = `mailto:${encodeURIComponent(email)}`;
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    if (phoneNumber && isValidPhone(phoneNumber)) {
      window.location.href = `tel:${encodeURIComponent(phoneNumber)}`;
    }
  };

  const handleDirections = (e) => {
    e.stopPropagation();
    if (formattedAddress) {
      const encodedAddress = encodeURIComponent(formattedAddress);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  const handleCardClick = () => {
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          border: isCurrentUser ? '2px solid' : 'none',
          borderColor: isCurrentUser ? 'primary.main' : 'transparent',
          transition: 'transform 0.2s, box-shadow 0.2s',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
        onClick={handleCardClick}
      >
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          {/* Header with Avatar and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={photoURL}
              alt={displayName}
              sx={{ width: 56, height: 56, mr: 2 }}
            >
              {displayName?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {displayName}
                {isCurrentUser && (
                  <Chip
                    label="You"
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Typography>
              {relationship && (
                <Typography variant="body2" color="text.secondary">
                  {relationship}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 2 }}>
            {/* Email */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
              {email ? (
                <Typography
                  variant="body2"
                  sx={{ wordBreak: 'break-all', color: 'primary.main' }}
                >
                  {email}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Email hidden
                </Typography>
              )}
            </Box>

            {/* Phone */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
              {phoneNumber ? (
                <Typography variant="body2" sx={{ color: 'primary.main' }}>
                  {phoneNumber}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Phone hidden
                </Typography>
              )}
            </Box>

            {/* Address */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <LocationIcon sx={{ fontSize: 18, mr: 1, mt: 0.2, color: 'text.secondary' }} />
              {formattedAddress ? (
                <Typography variant="body2" sx={{ color: 'primary.main' }}>
                  {formattedAddress}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Address not provided
                </Typography>
              )}
            </Box>
          </Box>

          {/* Groups */}
          {groups && groups.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Groups:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {groups.slice(0, 3).map((groupId, index) => {
                  // Handle both string IDs and objects
                  const gId = typeof groupId === 'string' ? groupId : groupId.groupId;
                  const groupName = typeof groupId === 'object' && groupId.name ? groupId.name : gId;

                  return (
                    <Chip
                      key={index}
                      label={groupName}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  );
                })}
                {groups.length > 3 && (
                  <Chip
                    label={`+${groups.length - 3} more`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Tooltip title={email ? 'Send email' : 'Email hidden'}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleEmail}
                  disabled={!email}
                  sx={{ flex: 1 }}
                >
                  <EmailIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={phoneNumber ? 'Call' : 'Phone hidden'}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleCall}
                  disabled={!phoneNumber}
                  sx={{ flex: 1 }}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={formattedAddress ? 'Get directions' : 'Address not provided'}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleDirections}
                  disabled={!formattedAddress}
                  sx={{ flex: 1 }}
                >
                  <LocationIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={photoURL}
                alt={displayName}
                sx={{ width: 64, height: 64 }}
              >
                {displayName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                  {displayName}
                </Typography>
                {relationship && (
                  <Typography variant="body2" color="text.secondary">
                    {relationship}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={handleCloseDetail}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {/* Email */}
            <ListItem>
              <ListItemIcon>
                <EmailIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Email"
                secondary={
                  email ? (
                    <Typography
                      component="a"
                      href={`mailto:${email}`}
                      sx={{ color: 'primary.main', textDecoration: 'none' }}
                    >
                      {email}
                    </Typography>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Hidden
                    </Typography>
                  )
                }
              />
            </ListItem>

            <Divider variant="inset" component="li" />

            {/* Phone */}
            <ListItem>
              <ListItemIcon>
                <PhoneIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Phone"
                secondary={
                  phoneNumber ? (
                    <Typography
                      component="a"
                      href={`tel:${phoneNumber}`}
                      sx={{ color: 'primary.main', textDecoration: 'none' }}
                    >
                      {phoneNumber}
                    </Typography>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Hidden
                    </Typography>
                  )
                }
              />
            </ListItem>

            <Divider variant="inset" component="li" />

            {/* Address */}
            <ListItem>
              <ListItemIcon>
                <HomeIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Address"
                secondary={
                  formattedAddress ? (
                    <Typography
                      component="a"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'none' }}
                    >
                      {formattedAddress}
                    </Typography>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Not provided
                    </Typography>
                  )
                }
              />
            </ListItem>

            {/* Birthday - if available */}
            {birthday && (
              <>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemIcon>
                    <BirthdayIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Birthday" secondary={birthday} />
                </ListItem>
              </>
            )}

            {/* Occupation - if available */}
            {occupation && (
              <>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemIcon>
                    <WorkIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Occupation"
                    secondary={
                      professionalProfile?.company
                        ? `${occupation} at ${professionalProfile.company}`
                        : occupation
                    }
                  />
                </ListItem>
              </>
            )}

            {/* Industry - if available */}
            {professionalProfile?.industry && (
              <>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemIcon>
                    <IndustryIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Industry" secondary={professionalProfile.industry} />
                </ListItem>
              </>
            )}

            {/* Business Information Section */}
            {businessInfo && (businessInfo.businessName || businessInfo.businessPhone || businessInfo.businessEmail || businessInfo.website || businessInfo.linkedIn) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, mb: 1 }}>
                  Business Information
                </Typography>

                {/* Business Name */}
                {businessInfo.businessName && (
                  <ListItem>
                    <ListItemIcon>
                      <BusinessIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Business"
                      secondary={businessInfo.businessName}
                    />
                  </ListItem>
                )}

                {/* Business Phone */}
                {businessInfo.businessPhone && (
                  <>
                    <Divider variant="inset" component="li" />
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Business Phone"
                        secondary={
                          <Typography
                            component="a"
                            href={`tel:${businessInfo.businessPhone}`}
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                          >
                            {businessInfo.businessPhone}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </>
                )}

                {/* Business Email */}
                {businessInfo.businessEmail && (
                  <>
                    <Divider variant="inset" component="li" />
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Business Email"
                        secondary={
                          <Typography
                            component="a"
                            href={`mailto:${businessInfo.businessEmail}`}
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                          >
                            {businessInfo.businessEmail}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </>
                )}

                {/* Website */}
                {businessInfo.website && (
                  <>
                    <Divider variant="inset" component="li" />
                    <ListItem>
                      <ListItemIcon>
                        <WebsiteIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Website"
                        secondary={
                          <Typography
                            component="a"
                            href={businessInfo.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                          >
                            {businessInfo.website}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </>
                )}

                {/* LinkedIn */}
                {businessInfo.linkedIn && (
                  <>
                    <Divider variant="inset" component="li" />
                    <ListItem>
                      <ListItemIcon>
                        <LinkedInIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="LinkedIn"
                        secondary={
                          <Typography
                            component="a"
                            href={businessInfo.linkedIn}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                          >
                            View Profile
                          </Typography>
                        }
                      />
                    </ListItem>
                  </>
                )}
              </>
            )}
          </List>

          {/* Groups */}
          {groups && groups.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Member of:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {groups.map((groupId, index) => {
                  const gId = typeof groupId === 'string' ? groupId : groupId.groupId;
                  const groupName = typeof groupId === 'object' && groupId.name ? groupId.name : gId;

                  return (
                    <Chip
                      key={index}
                      label={groupName}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {email && isValidEmail(email) && (
            <Button
              startIcon={<EmailIcon />}
              onClick={() => window.location.href = `mailto:${encodeURIComponent(email)}`}
            >
              Email
            </Button>
          )}
          {phoneNumber && isValidPhone(phoneNumber) && (
            <Button
              startIcon={<PhoneIcon />}
              onClick={() => window.location.href = `tel:${encodeURIComponent(phoneNumber)}`}
            >
              Call
            </Button>
          )}
          {formattedAddress && (
            <Button
              startIcon={<LocationIcon />}
              onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`, '_blank')}
            >
              Directions
            </Button>
          )}
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactCard;
